import { getDB } from './init';
import { STORE_NAMES, SyncQueueItem } from './schema';

/**
 * Sync Queue Store Operations
 * 
 * Manages pending operations that need to be synced to the server
 */

export async function addToSyncQueue(item: Omit<SyncQueueItem, 'queueId'>): Promise<number> {
  const db = await getDB();
  return db.add(STORE_NAMES.SYNC_QUEUE, item as SyncQueueItem);
}

export async function getSyncQueueItem(queueId: number): Promise<SyncQueueItem | undefined> {
  const db = await getDB();
  return db.get(STORE_NAMES.SYNC_QUEUE, queueId);
}

export async function getAllSyncQueueItems(): Promise<SyncQueueItem[]> {
  const db = await getDB();
  const items = await db.getAll(STORE_NAMES.SYNC_QUEUE);
  // Sort by creation time (oldest first)
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

export async function getSyncQueueItemsByType(
  entityType: SyncQueueItem['entityType']
): Promise<SyncQueueItem[]> {
  const db = await getDB();
  return db.getAllFromIndex(STORE_NAMES.SYNC_QUEUE, 'entityType', entityType);
}

export async function updateSyncQueueItem(
  queueId: number,
  updates: Partial<SyncQueueItem>
): Promise<void> {
  const db = await getDB();
  const item = await db.get(STORE_NAMES.SYNC_QUEUE, queueId);
  if (!item) {
    throw new Error(`Sync queue item ${queueId} not found`);
  }
  await db.put(STORE_NAMES.SYNC_QUEUE, { ...item, ...updates });
}

export async function incrementRetryCount(queueId: number, error?: string): Promise<void> {
  const db = await getDB();
  const item = await db.get(STORE_NAMES.SYNC_QUEUE, queueId);
  if (!item) {
    throw new Error(`Sync queue item ${queueId} not found`);
  }
  await db.put(STORE_NAMES.SYNC_QUEUE, {
    ...item,
    retryCount: item.retryCount + 1,
    lastError: error,
  });
}

export async function removeSyncQueueItem(queueId: number): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAMES.SYNC_QUEUE, queueId);
}

export async function clearSyncQueue(): Promise<void> {
  const db = await getDB();
  await db.clear(STORE_NAMES.SYNC_QUEUE);
}

export async function getSyncQueueSize(): Promise<number> {
  const db = await getDB();
  return db.count(STORE_NAMES.SYNC_QUEUE);
}

/**
 * Get items that should be retried (not exceeded max retries)
 */
export async function getRetryableItems(maxRetries = 5): Promise<SyncQueueItem[]> {
  const db = await getDB();
  const allItems = await db.getAll(STORE_NAMES.SYNC_QUEUE);
  return allItems.filter(item => item.retryCount < maxRetries);
}

/**
 * Get items that have failed too many times
 */
export async function getFailedItems(maxRetries = 5): Promise<SyncQueueItem[]> {
  const db = await getDB();
  const allItems = await db.getAll(STORE_NAMES.SYNC_QUEUE);
  return allItems.filter(item => item.retryCount >= maxRetries);
}
